import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, signal } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';

import { AuthService } from '../core/auth/auth.service';
import { MenuItem, MenuService } from '../core/menu/menu.service';
import { NavGroup, NavItem, SECTION_META, STATIC_NAV } from '../core/nav.config';
import { ThemeService } from '../core/theme.service';
import { CommandPalette } from '../shared/command-palette/command-palette';
import { Toast } from '../shared/toast/toast';

/**
 * Casca (shell) navegável da plataforma: top bar global + sidebar agrupada +
 * área de conteúdo roteada. A sidebar é desenhada para absorver novos
 * simuladores e dashboards sem redesenho. No mobile vira um drawer com gestão de
 * foco e fechamento por Esc.
 */
@Component({
  selector: 'ub-shell',
  imports: [RouterOutlet, RouterLink, RouterLinkActive, Toast, CommandPalette],
  templateUrl: './shell.html',
  styleUrl: './shell.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'onEscape()',
    '(document:click)': 'closeUserMenu()',
    '(document:keydown.meta.k)': 'onCommandKey($event)',
    '(document:keydown.control.k)': 'onCommandKey($event)',
  },
})
export class Shell {
  private readonly themeService = inject(ThemeService);
  private readonly auth = inject(AuthService);
  private readonly menu = inject(MenuService);
  private readonly router = inject(Router);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  protected readonly theme = this.themeService.theme;
  protected readonly isLight = computed(() => this.theme() === 'light');
  protected readonly user = this.auth.user;

  /**
   * Menu lateral data-driven: o grupo estático "Geral" + os grupos do catálogo
   * (`GET /me/menu`), agrupados por seção e ordenados. Itens `COMING_SOON` viram
   * teaser "EM BREVE"; `ACTIVE`/`BETA` só aparecem quando acessíveis ao usuário;
   * `HIDDEN`/`DISABLED` não chegam (o backend já os exclui). Grupos vazios caem.
   */
  protected readonly visibleNav = computed<readonly NavGroup[]>(() => {
    const dynamic = this.buildSections(this.menu.items());
    return [...STATIC_NAV, ...dynamic].filter((group) => group.items.length > 0);
  });

  /** Agrupa os itens do catálogo por seção (rótulo/ordem em `SECTION_META`). */
  private buildSections(items: readonly MenuItem[]): NavGroup[] {
    const bySection = new Map<string, NavItem[]>();
    for (const item of [...items].sort((a, b) => a.order - b.order)) {
      const navItem = this.toNavItem(item);
      if (!navItem) {
        continue;
      }
      const list = bySection.get(item.section) ?? [];
      list.push(navItem);
      bySection.set(item.section, list);
    }
    return [...bySection.entries()]
      .map(([section, sectionItems]) => ({ section, sectionItems, meta: SECTION_META[section] }))
      .sort((a, b) => (a.meta?.order ?? 999) - (b.meta?.order ?? 999))
      .map((group) => ({ title: group.meta?.label ?? group.section, items: group.sectionItems }));
  }

  /** Converte um item do catálogo em item de menu, ou `null` se não deve aparecer. */
  private toNavItem(item: MenuItem): NavItem | null {
    if (item.status === 'COMING_SOON') {
      return { label: item.byname, icon: item.icon, badge: 'EM BREVE', disabled: true };
    }
    if (!item.accessible) {
      return null;
    }
    return { label: item.byname, icon: item.icon, route: item.route, badge: item.status === 'BETA' ? 'BETA' : undefined };
  }

  /** Drawer da sidebar no mobile (no desktop a sidebar é fixa e isto é ignorado). */
  protected readonly sidebarOpen = signal(false);

  /** Menu do chip do usuário (Configurações / Sair). */
  protected readonly userMenuOpen = signal(false);

  /** Paleta de comandos (⌘K): busca rápida de páginas, documentação e ações. */
  protected readonly paletteOpen = signal(false);

  protected openPalette(): void {
    this.paletteOpen.set(true);
  }

  protected closePalette(): void {
    this.paletteOpen.set(false);
  }

  /** Atalho ⌘K / Ctrl+K: alterna a paleta e impede o comportamento padrão do navegador. */
  protected onCommandKey(event: Event): void {
    event.preventDefault();
    this.paletteOpen.update((open) => !open);
  }

  protected toggleUserMenu(): void {
    this.userMenuOpen.update((open) => !open);
  }

  protected closeUserMenu(): void {
    this.userMenuOpen.set(false);
  }

  protected toggleSidebar(): void {
    const open = !this.sidebarOpen();
    this.sidebarOpen.set(open);
    if (open) {
      queueMicrotask(() => this.host.nativeElement.querySelector<HTMLElement>('.sidebar .nav-item')?.focus());
    }
  }

  protected closeSidebar(): void {
    if (!this.sidebarOpen()) {
      return;
    }
    this.sidebarOpen.set(false);
    queueMicrotask(() => this.host.nativeElement.querySelector<HTMLElement>('.hamburger')?.focus());
  }

  protected onEscape(): void {
    this.closeSidebar();
    this.closeUserMenu();
  }

  protected setLight(): void {
    this.themeService.setTheme('light');
  }

  protected setDark(): void {
    this.themeService.setTheme('dark');
  }

  protected async logout(): Promise<void> {
    try {
      await this.auth.logout();
    } catch {
      this.auth.user.set(null);
      await this.router.navigateByUrl('/login');
    }
  }
}
